import { FilePlus, FileSearch, X } from "lucide-react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { useSettingsStore } from "../../../stores/settingsStore";

interface DuplicateReportPortalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadExisting: () => void;
  onForceRegenerate: () => void;
  existingReportId: string;
  existingReportStatus: string;
  isCsvContext?: boolean;
  isLoadingExisting?: boolean;
  isRegenerating?: boolean;
}

export default function DuplicateReportPortal({
  isOpen,
  onClose,
  onLoadExisting,
  onForceRegenerate,
  existingReportId,
  existingReportStatus,
  isCsvContext = false,
  isLoadingExisting = false,
  isRegenerating = false,
}: DuplicateReportPortalProps) {
  const { t } = useTranslation();
  const darkMode = useSettingsStore((s) => s.darkMode);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isBusy = isLoadingExisting || isRegenerating;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity"
        onClick={isBusy ? undefined : onClose}
        onKeyDown={(e) => !isBusy && e.key === "Escape" && onClose()}
        role="button"
        tabIndex={0}
        aria-label={t("reports.closeModal") || "Close modal"}
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className={`relative w-full max-w-md rounded-3xl shadow-2xl pointer-events-auto transform transition-all ${
            darkMode
              ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-gray-700"
              : "bg-white"
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="duplicate-report-title"
        >
          {/* Header */}
          <div
            className={`px-8 py-6 border-b ${
              darkMode
                ? "bg-gray-900/95 backdrop-blur border-gray-700"
                : "bg-white/95 backdrop-blur border-gray-200"
            } rounded-t-3xl`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div
                  className={`p-3 rounded-2xl shadow-lg ${
                    darkMode
                      ? "bg-[var(--brand-secondary)]/10"
                      : "bg-orange-100"
                  }`}
                >
                  <FileSearch className="w-6 h-6 text-[var(--brand-secondary)]" />
                </div>
                <h2
                  id="duplicate-report-title"
                  className={`text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
                >
                  {t("reports.duplicateReportTitle") || "Report Already Exists"}
                </h2>
              </div>
              <button
                onClick={onClose}
                disabled={isBusy}
                className={`p-2 rounded-xl transition-colors ${
                  darkMode
                    ? "hover:bg-gray-700 text-gray-400 hover:text-white disabled:opacity-40"
                    : "hover:bg-gray-100 text-gray-500 hover:text-gray-700 disabled:opacity-40"
                }`}
                aria-label={t("reports.closeModal") || "Close"}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-8 py-6">
            <p
              className={`text-base leading-relaxed mb-4 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
            >
              {isCsvContext
                ? t("reports.csvDuplicateReportMessage") ||
                  "A Shadow Report already exists for this CSV import and period. Load it or generate a new one from the same CSV."
                : t("reports.duplicateReportMessage") ||
                  "A report for this client and period already exists. What would you like to do?"}
            </p>
            <div
              className={`flex items-center gap-2 text-sm px-3 py-2 rounded-xl ${
                darkMode
                  ? "bg-gray-700/50 text-gray-400"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              <span className="font-medium">
                {t("reports.existingReportId") || "Existing report:"}
              </span>
              <span className="font-mono truncate">{existingReportId}</span>
              {existingReportStatus && (
                <span
                  className={`ml-auto shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold ${
                    existingReportStatus === "PUBLISHED"
                      ? "bg-green-500/20 text-green-400"
                      : existingReportStatus === "DRAFT"
                        ? "bg-[var(--brand-primary)]/20 text-[var(--brand-primary)]"
                        : "bg-gray-500/20 text-gray-400"
                  }`}
                >
                  {existingReportStatus}
                </span>
              )}
            </div>
          </div>

          {/* Footer */}
          <div
            className={`px-8 py-6 mt-2 border-t ${
              darkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="flex flex-col gap-3">
              {/* Load existing */}
              <button
                type="button"
                onClick={onLoadExisting}
                disabled={isBusy}
                className={`flex items-center justify-center gap-2 w-full px-6 py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed bg-gradient-to-r from-[var(--brand-primary)] to-teal-500 hover:from-teal-500 hover:to-[var(--brand-primary)] text-white shadow`}
              >
                {isLoadingExisting ? (
                  <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <FileSearch className="w-4 h-4" />
                )}
                {t("reports.loadExistingReport") || "Load Existing Report"}
              </button>

              {/* Force regenerate */}
              <button
                type="button"
                onClick={onForceRegenerate}
                disabled={isBusy}
                className={`flex items-center justify-center gap-2 w-full px-6 py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                  darkMode
                    ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {isRegenerating ? (
                  <span className="inline-block w-4 h-4 border-2 border-gray-400/40 border-t-gray-600 rounded-full animate-spin" />
                ) : (
                  <FilePlus className="w-4 h-4" />
                )}
                {isCsvContext
                  ? t("reports.generateNewCsvShadowReport") ||
                    "Generate New Shadow Report for This Period"
                  : t("reports.forceRegenerateReport") ||
                    "Generate New Report (Overwrite)"}
              </button>

              {/* Cancel */}
              <button
                type="button"
                onClick={onClose}
                disabled={isBusy}
                className={`text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                  darkMode
                    ? "text-gray-500 hover:text-gray-300"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {t("reports.cancel") || "Cancel"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
